/**
 * Shrimp Zone — dispatch date logic
 *
 * Pure functions. No DOM, no theme dependencies.
 * All wall-clock reasoning happens in Australia/Melbourne regardless of
 * where the visitor is. Never trust the browser's local clock or a
 * hardcoded UTC offset — AEST/AEDT shifts twice a year.
 *
 * Rules implemented:
 *   Livestock        — dispatched Mon and Tue, cutoff 11:00 Melbourne.
 *                      Cutoff means PAYMENT CLEARED, not order placed.
 *   Dry goods only   — dispatched next business day, delivery +5 business days.
 *   Mixed cart       — ships together on the livestock slot.
 *   Pre-order lines  — dispatch is the first eligible slot on or after the
 *                      latest ready_date in the cart.
 *   Blackouts        — holidays and heat pauses push dispatch forward.
 */

(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.SZDispatch = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var TZ = 'Australia/Melbourne';
  var DAY_MS = 86400000;
  var MAX_LOOKAHEAD_DAYS = 400; // hard stop so a long pause can't loop forever

  /* ------------------------------------------------------------------ *
   * Timezone helpers
   * ------------------------------------------------------------------ */

  var partsFormatter = new Intl.DateTimeFormat('en-AU', {
    timeZone: TZ,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short'
  });

  var WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /** Melbourne wall-clock parts for a given UTC timestamp. */
  function melbourneParts(utcMs) {
    var out = {};
    var parts = partsFormatter.formatToParts(new Date(utcMs));
    for (var i = 0; i < parts.length; i++) {
      out[parts[i].type] = parts[i].value;
    }
    return {
      year: +out.year,
      month: +out.month,
      day: +out.day,
      hour: +out.hour % 24,
      minute: +out.minute,
      second: +out.second,
      weekday: WEEKDAY_INDEX[out.weekday]
    };
  }

  /** Offset of Melbourne from UTC, in ms, at a given instant. */
  function offsetAt(utcMs) {
    var p = melbourneParts(utcMs);
    var asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asIfUtc - utcMs;
  }

  /**
   * Convert a Melbourne wall-clock datetime to a UTC timestamp.
   * Two-pass so DST transition days resolve correctly.
   */
  function melbourneToUtc(year, month, day, hour, minute) {
    var naive = Date.UTC(year, month - 1, day, hour, minute || 0, 0);
    var ts = naive - offsetAt(naive);
    ts = naive - offsetAt(ts);
    return ts;
  }

  /** 'YYYY-MM-DD' for a Melbourne calendar date. */
  function isoDate(year, month, day) {
    return (
      year +
      '-' +
      String(month).padStart(2, '0') +
      '-' +
      String(day).padStart(2, '0')
    );
  }

  /** Walk a Melbourne calendar date forward by n days, DST-safe. */
  function addDays(dateParts, n) {
    var noon = melbourneToUtc(dateParts.year, dateParts.month, dateParts.day, 12, 0);
    var moved = melbourneParts(noon + n * DAY_MS);
    return { year: moved.year, month: moved.month, day: moved.day, weekday: moved.weekday };
  }

  /* ------------------------------------------------------------------ *
   * Blackouts
   * ------------------------------------------------------------------ */

  /**
   * Blackouts are closures only — public holidays, leave, days you aren't
   * packing. They apply to everything.
   *
   * Heat pauses are NOT blackouts. A pause is a manual switch you flip when
   * a forecast warrants it (see config.livestockPause), not a date range
   * guessed months ahead.
   *
   * Accepts a date string or an inclusive {from, to} range.
   */
  function blackoutFor(iso, blackouts) {
    if (!blackouts) return null;
    for (var i = 0; i < blackouts.length; i++) {
      var b = blackouts[i];
      var entry = typeof b === 'string' ? { from: b, to: b } : b;
      if (entry.from && entry.to && iso >= entry.from && iso <= entry.to) return entry;
    }
    return null;
  }

  function isBlackedOut(iso, blackouts) {
    return blackoutFor(iso, blackouts) !== null;
  }

  /* ------------------------------------------------------------------ *
   * Slot finders
   * ------------------------------------------------------------------ */

  /**
   * Next livestock dispatch slot at or after `fromMs`.
   * Returns { ts, iso, weekday } or null if none inside the lookahead.
   */
  function nextLivestockSlot(fromMs, config) {
    var shipDays = config.livestock.shipDays;      // e.g. [1, 2]
    var cutoffHour = config.livestock.cutoffHour;  // e.g. 11
    var cutoffMinute = config.livestock.cutoffMinute || 0;
    var cursor = melbourneParts(fromMs);

    for (var i = 0; i < MAX_LOOKAHEAD_DAYS; i++) {
      var iso = isoDate(cursor.year, cursor.month, cursor.day);
      var isShipDay = shipDays.indexOf(cursor.weekday) !== -1;

      if (isShipDay && !isBlackedOut(iso, config.blackouts)) {
        var slotTs = melbourneToUtc(
          cursor.year, cursor.month, cursor.day, cutoffHour, cutoffMinute
        );
        if (slotTs > fromMs) {
          return { ts: slotTs, iso: iso, weekday: cursor.weekday };
        }
      }
      cursor = addDays(cursor, 1);
    }
    return null;
  }

  /** Is this Melbourne calendar date a business day (Mon–Fri, not blacked out)? */
  function isBusinessDay(dateParts, config) {
    if (dateParts.weekday === 0 || dateParts.weekday === 6) return false;
    return !isBlackedOut(
      isoDate(dateParts.year, dateParts.month, dateParts.day),
      config.blackouts
    );
  }

  /**
   * Next dry-goods dispatch day at or after `fromMs`.
   * Same-day if before the dry cutoff, otherwise the following business day.
   */
  function nextDryDispatch(fromMs, config) {
    var cutoffHour = config.dryGoods.cutoffHour;
    var cutoffMinute = config.dryGoods.cutoffMinute || 0;
    var cursor = melbourneParts(fromMs);

    for (var i = 0; i < MAX_LOOKAHEAD_DAYS; i++) {
      if (isBusinessDay(cursor, config)) {
        var slotTs = melbourneToUtc(
          cursor.year, cursor.month, cursor.day, cutoffHour, cutoffMinute
        );
        if (slotTs > fromMs) {
          return { ts: slotTs, iso: isoDate(cursor.year, cursor.month, cursor.day) };
        }
      }
      cursor = addDays(cursor, 1);
    }
    return null;
  }

  /** Add n business days to a dispatch date, for delivery estimates. */
  function addBusinessDays(fromIso, n, config) {
    var bits = fromIso.split('-');
    var cursor = melbourneParts(
      melbourneToUtc(+bits[0], +bits[1], +bits[2], 12, 0)
    );
    var counted = 0;
    var guard = 0;
    while (counted < n && guard < MAX_LOOKAHEAD_DAYS) {
      cursor = addDays(cursor, 1);
      if (isBusinessDay(cursor, config)) counted++;
      guard++;
    }
    return isoDate(cursor.year, cursor.month, cursor.day);
  }

  /* ------------------------------------------------------------------ *
   * Cart analysis
   * ------------------------------------------------------------------ */

  /**
   * Line shape expected:
   *   { id, quantity, kind: 'livestock' | 'dry', readyDate: 'YYYY-MM-DD' | null }
   *
   * `kind` and `readyDate` come from product metafields rendered into the
   * page by dispatch-config.liquid — not from the pre-order app's own state,
   * so the theme and the app can't disagree.
   */
  function analyseCart(lines) {
    var hasLivestock = false;
    var hasDry = false;
    var hasPreorder = false;
    var latestReady = null;

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (line.kind === 'livestock') hasLivestock = true;
      else hasDry = true;

      if (line.readyDate) {
        hasPreorder = true;
        if (!latestReady || line.readyDate > latestReady) latestReady = line.readyDate;
      }
    }

    return {
      hasLivestock: hasLivestock,
      hasDry: hasDry,
      hasPreorder: hasPreorder,
      isMixed: hasLivestock && hasDry,
      readyDate: latestReady
    };
  }

  /* ------------------------------------------------------------------ *
   * Main resolver
   * ------------------------------------------------------------------ */

  /**
   * Resolve dispatch for a cart.
   *
   * Returns:
   * {
   *   state: 'countdown' | 'scheduled' | 'preorder' | 'paused' | 'unavailable',
   *   dispatchIso, dispatchTs, cutoffTs, deliveryIso,
   *   shipTogether, readyDate, pauseUntil, msUntilCutoff
   * }
   */
  function resolve(lines, config, nowMs) {
    var now = typeof nowMs === 'number' ? nowMs : Date.now();
    var cart = analyseCart(lines);
    var pause = config.livestockPause;

    // Heat pause: a manual switch, flipped when the forecast warrants it.
    // Only livestock stops — dry-goods orders ship straight through.
    if (cart.hasLivestock && pause && pause.active) {
      var resumeTs = null;
      if (pause.resumeDate) {
        var rp = pause.resumeDate.split('-');
        resumeTs = melbourneToUtc(+rp[0], +rp[1], +rp[2], 0, 0);
      }

      // Resume date already passed and nobody cleared the switch — treat the
      // pause as stale rather than blocking sales indefinitely.
      var stale = resumeTs !== null && resumeTs <= now;

      if (!stale) {
        var resumeSlot = resumeTs !== null
          ? nextLivestockSlot(resumeTs, config)
          : null;

        return {
          state: 'paused',
          dispatchIso: resumeSlot ? resumeSlot.iso : null,
          dispatchTs: resumeSlot ? resumeSlot.ts : null,
          deliveryIso: resumeSlot
            ? addBusinessDays(resumeSlot.iso, config.livestock.transitBusinessDays, config)
            : null,
          shipTogether: cart.isMixed,
          readyDate: cart.readyDate,
          pauseIndefinite: resumeTs === null,
          pauseReason: pause.reason || null,
          msUntilCutoff: null
        };
      }
    }

    // Pre-orders gate the earliest possible dispatch for the whole order.
    var earliest = now;
    if (cart.readyDate) {
      var r = cart.readyDate.split('-');
      var readyTs = melbourneToUtc(+r[0], +r[1], +r[2], 0, 0);
      if (readyTs > earliest) earliest = readyTs;
    }

    var slot;
    var deliveryDays;

    if (cart.hasLivestock) {
      slot = nextLivestockSlot(earliest, config);
      deliveryDays = config.livestock.transitBusinessDays;
    } else {
      slot = nextDryDispatch(earliest, config);
      deliveryDays = config.dryGoods.transitBusinessDays;
    }

    if (!slot) {
      return {
        state: 'unavailable',
        shipTogether: cart.isMixed,
        readyDate: cart.readyDate
      };
    }

    // A livestock dispatch landing inside a heat pause has already been
    // skipped by nextLivestockSlot. Surface the pause if we're inside one now.
    var todayParts = melbourneParts(now);
    var todayIso = isoDate(todayParts.year, todayParts.month, todayParts.day);
    var activePause = cart.hasLivestock
      ? blackoutFor(todayIso, config.blackouts)
      : null;

    var msUntilCutoff = slot.ts - now;
    var state;

    if (cart.hasPreorder) {
      state = 'preorder';
    } else if (activePause) {
      state = 'paused';
    } else if (msUntilCutoff <= config.countdownWindowMs) {
      state = 'countdown';
    } else {
      state = 'scheduled';
    }

    return {
      state: state,
      dispatchIso: slot.iso,
      dispatchTs: slot.ts,
      cutoffTs: slot.ts,
      deliveryIso: addBusinessDays(slot.iso, deliveryDays, config),
      shipTogether: cart.isMixed,
      readyDate: cart.readyDate,
      pauseUntil: activePause && activePause.to ? activePause.to : null,
      msUntilCutoff: msUntilCutoff
    };
  }

  /**
   * PRECISION FOLLOWS MAGNITUDE (2026-08-21, owner asked for a countdown in
   * place of a bare date).
   *
   * The trap in "always show a countdown" is that a long one does the OPPOSITE
   * of what it is for: "order within 4 days 6 hours" reads as no rush, and
   * ticking seconds beside it reads as a gimmick, because nobody is deciding
   * on a Tuesday what to do at 11:00 on a Monday.
   *
   * So the unit tracks how close the deadline actually is, and the display
   * never shows a digit the shopper could act on:
   *   > 48h   "2 days"        — a duration, not a deadline
   *   1–48h   "6h 14m"        — close enough to plan around
   *   < 1h    "47m 12s"       — close enough that seconds mean something
   *
   * Seconds only ever appear inside the last hour, which is the only window
   * where they are information rather than pressure.
   */
  function formatCountdown(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));

    if (total >= 48 * 3600) {
      // DAYS **AND HOURS**, because this number is printed beside a named day
      // the reader can check — "Order within 2 days to ship Monday 24 August".
      //
      // A bare floored day count fails that check and reads as a bug: on Friday
      // afternoon the cutoff is 67 hours away, "2 days" lands on Sunday, and the
      // line next to it says Monday. Shipped exactly that on 2026-08-21 and the
      // owner spotted it immediately.
      //
      // Flooring is still right for the DAYS component — overstating how long is
      // left is the one direction that makes someone miss a cutoff — but the
      // remainder has to be shown, or the floor becomes a contradiction rather
      // than a safe approximation. 67h now reads "2 days 19h", and Friday 16:00
      // plus 2 days 19 hours is exactly Monday 11:00.
      var days = Math.floor(total / 86400);
      var restH = Math.floor((total % 86400) / 3600);
      var label = days + ' day' + (days === 1 ? '' : 's');
      // Exactly on a day boundary the remainder is noise, so drop it.
      return restH > 0 ? label + ' ' + restH + 'h' : label;
    }

    // TOTAL hours, not hours-within-the-day. `(total % 86400) / 3600` is the
    // obvious way to write this and it is wrong for every value between 24 and
    // 48 hours: 47h renders as "23h" and 24h renders as "0h 00m", telling a
    // customer the cutoff is now when it is a day away. Caught the moment
    // these functions moved somewhere Node could execute them.
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;

    if (total >= 3600) return h + 'h ' + String(m).padStart(2, '0') + 'm';
    return m + 'm ' + String(s).padStart(2, '0') + 's';
  }

  /**
   * How often the number has to be redrawn to stay honest. Repainting a
   * three-day figure every second is work nobody can see.
   */
  function tickFor(ms) {
    if (ms < 3600000) return 1000;        // last hour: seconds are on screen
    if (ms < 48 * 3600000) return 60000;  // hours and minutes: once a minute
    return 600000;                        // whole days: every ten minutes
  }

  return {
    resolve: resolve,
    formatCountdown: formatCountdown,
    tickFor: tickFor,
    analyseCart: analyseCart,
    nextLivestockSlot: nextLivestockSlot,
    nextDryDispatch: nextDryDispatch,
    addBusinessDays: addBusinessDays,
    melbourneParts: melbourneParts,
    melbourneToUtc: melbourneToUtc,
    isoDate: isoDate
  };
});
